import React, { useCallback, useEffect, useRef, useState } from 'react';

function App() {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const textInputRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('brush');
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(3);
  const [eraserSize, setEraserSize] = useState(20);
  const [textColor, setTextColor] = useState('#000000');
  const [textSize, setTextSize] = useState(24);
  const colorOptions = ['#000000', '#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3', '#9c27b0', '#ffffff'];
  const [textItems, setTextItems] = useState([]);
  const [activeTextInput, setActiveTextInput] = useState(null);
  const [textValue, setTextValue] = useState('');
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pages, setPages] = useState([{ canvasImage: null, textItems: [] }]);
  const [draggingText, setDraggingText] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [shapeStart, setShapeStart] = useState(null);
  const [shapeBox, setShapeBox] = useState(null);
  const [shapeFill, setShapeFill] = useState(false);
  const [shapeFillColor, setShapeFillColor] = useState('#ffffff');
  const [tooltip, setTooltip] = useState({ text: '', x: 0, y: 0, visible: false });
  const lastPosRef = useRef({ x: 0, y: 0 });

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const previous = getCanvasImage();
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width * 2;
    canvas.height = height * 2;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const context = canvas.getContext('2d');
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(2, 2);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.fillStyle = 'white';
    context.fillRect(0, 0, width, height);
    contextRef.current = context;

    if (previous) {
      const image = new Image();
      image.onload = () => {
        context.save();
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        context.restore();
      };
      image.src = previous;
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  const getCanvasImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL();
  };

  const restoreCanvasImage = (imageDataUrl) => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context || !imageDataUrl) return;
    const image = new Image();
    image.onload = () => {
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.restore();
    };
    image.src = imageDataUrl;
  };

  const clearCanvasOnly = () => {
    const canvas = canvasRef.current;
    if (!canvas || !contextRef.current) return;
    const width = canvas.width / 2;
    const height = canvas.height / 2;
    contextRef.current.clearRect(0, 0, width, height);
    contextRef.current.fillStyle = 'white';
    contextRef.current.fillRect(0, 0, width, height);
  };

  const drawShapeOnCanvas = () => {
    if (!shapeBox || !contextRef.current) return;
    const normalized = normalizeBox(shapeBox);
    const context = contextRef.current;
    context.save();
    context.globalCompositeOperation = 'source-over';
    if (shapeFill) {
      context.fillStyle = shapeFillColor;
      context.fillRect(normalized.x, normalized.y, normalized.width, normalized.height);
    }
    context.strokeStyle = brushColor;
    context.lineWidth = brushSize;
    context.strokeRect(normalized.x, normalized.y, normalized.width, normalized.height);
    context.restore();
  };

  const saveCurrentPage = () => {
    const canvasImage = getCanvasImage();
    setPages((prevPages) => prevPages.map((page, index) => (
      index === currentPageIndex
        ? { ...page, canvasImage, textItems }
        : page
    )));
  };

  const loadPage = (pageIndex) => {
    const page = pages[pageIndex];
    setCurrentPageIndex(pageIndex);
    setTextItems(page.textItems || []);
    setSelectedTextId(null);
    setSelectionBox(null);

    if (page.canvasImage) {
      restoreCanvasImage(page.canvasImage);
    } else {
      clearCanvasOnly();
    }
  };

  const addPage = () => {
    saveCurrentPage();
    const newPage = { canvasImage: null, textItems: [] };
    setPages((prevPages) => {
      const nextPages = [...prevPages, newPage];
      setCurrentPageIndex(nextPages.length - 1);
      return nextPages;
    });
    setTextItems([]);
    setSelectedTextId(null);
    setSelectionBox(null);
    clearCanvasOnly();
  };

  const goToPage = (pageIndex) => {
    saveCurrentPage();
    loadPage(pageIndex);
  };

  useEffect(() => {
    const context = contextRef.current;
    if (!context) return;

    if (tool === 'eraser') {
      context.globalCompositeOperation = 'destination-out';
      context.strokeStyle = 'rgba(0,0,0,1)';
      context.lineWidth = eraserSize;
    } else {
      context.globalCompositeOperation = 'source-over';
      context.strokeStyle = brushColor;
      context.lineWidth = brushSize;
    }
  }, [tool, brushColor, brushSize, eraserSize]);

  useEffect(() => {
    if (activeTextInput && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [activeTextInput]);

  const getCanvasCoords = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const showTooltip = (text, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top,
      visible: true
    });
  };

  const hideTooltip = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const buttonStyle = (active) => ({
    width: '38px',
    height: '38px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: active ? '#1976d2' : 'transparent',
    color: active ? 'white' : '#333',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease'
  });

  const navButtonStyle = (enabled) => ({
    width: '34px',
    height: '34px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: enabled ? '#1976d2' : '#ddd',
    color: enabled ? 'white' : '#777',
    cursor: enabled ? 'pointer' : 'not-allowed'
  });

  const pillButtonStyle = (backgroundColor, disabled = false) => ({
    padding: '8px 14px',
    fontSize: '14px',
    backgroundColor: backgroundColor,
    color: disabled ? '#999' : 'white',
    border: 'none',
    borderRadius: '18px',
    cursor: disabled ? 'not-allowed' : 'pointer'
  });

  const startDrawing = (event) => {
    if (tool !== 'brush' && tool !== 'eraser') return;
    const { x, y } = getCanvasCoords(event);
    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
    lastPosRef.current = { x, y };
    setIsDrawing(true);
  };

  const draw = (event) => {
    if (!isDrawing) return;
    const { x, y } = getCanvasCoords(event);
    const last = lastPosRef.current;
    const midX = (last.x + x) / 2;
    const midY = (last.y + y) / 2;
    contextRef.current.quadraticCurveTo(last.x, last.y, midX, midY);
    contextRef.current.stroke();
    lastPosRef.current = { x, y };
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    clearCanvasOnly();
    setTextItems([]);
    setSelectedTextId(null);
    setSelectionBox(null);
    setPages((prevPages) => prevPages.map((page, index) => (
      index === currentPageIndex
        ? { ...page, canvasImage: getCanvasImage(), textItems: [] }
        : page
    )));
  };

  const handleCanvasMouseDown = (event) => {
    if (tool === 'text') return;
    if (tool === 'select') {
      const { x, y } = getCanvasCoords(event);
      setIsSelecting(true);
      setSelectionBox({ x, y, width: 0, height: 0 });
      setSelectedTextId(null);
      return;
    }
    if (tool === 'rectangle' || tool === 'square') {
      const { x, y } = getCanvasCoords(event);
      setShapeStart({ x, y });
      setShapeBox({ x, y, width: 0, height: 0 });
    }
  };

  const handleCanvasMouseMove = (event) => {
    if (tool === 'text') return;

    if ((tool === 'rectangle' || tool === 'square') && shapeStart) {
      const { x, y } = getCanvasCoords(event);
      let width = x - shapeStart.x;
      let height = y - shapeStart.y;
      if (tool === 'square') {
        const size = Math.max(Math.abs(width), Math.abs(height));
        width = width < 0 ? -size : size;
        height = height < 0 ? -size : size;
      }
      setShapeBox({ x: shapeStart.x, y: shapeStart.y, width, height });
    }

    if (isSelecting && selectionBox) {
      const { x, y } = getCanvasCoords(event);
      setSelectionBox((box) => ({
        ...box,
        width: x - box.x,
        height: y - box.y
      }));
    }

    if (draggingText) {
      const { x, y } = getCanvasCoords(event);
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      setTextItems((items) => items.map((item) => {
        if (item.id !== draggingText) return item;
        return { ...item, x: item.x + dx, y: item.y + dy };
      }));
      setDragStart({ x, y });
    }
  };

  const handleCanvasMouseUp = () => {
    if (isSelecting && selectionBox) {
      const normalized = normalizeBox(selectionBox);
      const selected = textItems.find((item) => {
        const textWidth = item.text.length * item.size * 0.55;
        const textHeight = item.size + 8;
        return (
          item.x >= normalized.x &&
          item.y >= normalized.y &&
          item.x + textWidth <= normalized.x + normalized.width &&
          item.y + textHeight <= normalized.y + normalized.height
        );
      });
      setSelectedTextId(selected ? selected.id : null);
      setIsSelecting(false);
      setSelectionBox(null);
    }

    if ((tool === 'rectangle' || tool === 'square') && shapeStart && shapeBox) {
      drawShapeOnCanvas();
      setShapeStart(null);
      setShapeBox(null);
    }

    if (draggingText) {
      setDraggingText(null);
    }
  };

  const normalizeBox = (box) => {
    const x = Math.min(box.x, box.x + box.width);
    const y = Math.min(box.y, box.y + box.height);
    const width = Math.abs(box.width);
    const height = Math.abs(box.height);
    return { x, y, width, height };
  };

  const handleCanvasClick = (event) => {
    if (tool === 'text') {
      const { x, y } = getCanvasCoords(event);
      setTextValue('');
      setActiveTextInput({ x, y });
    }
  };

  const handleTextMouseDown = (event, id) => {
    event.stopPropagation();
    if (tool === 'text-eraser') {
      removeTextItem(id);
      return;
    }
    if (tool === 'select') {
      const { x, y } = getCanvasCoords(event.nativeEvent || event);
      setSelectedTextId(id);
      setDraggingText(id);
      setDragStart({ x, y });
    }
  };

  const commitText = () => {
    if (!activeTextInput) return;
    const trimmed = textValue.trim();
    if (trimmed) {
      setTextItems((items) => [
        ...items,
        {
          id: `${Date.now()}-${Math.random()}`,
          text: trimmed,
          x: activeTextInput.x,
          y: activeTextInput.y,
          color: textColor,
          size: textSize
        }
      ]);
    }
    setActiveTextInput(null);
    setTextValue('');
  };

  const cancelText = () => {
    setActiveTextInput(null);
    setTextValue('');
  };

  const removeTextItem = (id) => {
    setTextItems((items) => items.filter((item) => item.id !== id));
    if (selectedTextId === id) {
      setSelectedTextId(null);
    }
  };

  const deleteSelectedText = () => {
    if (!selectedTextId) return;
    removeTextItem(selectedTextId);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        backgroundColor: 'rgba(255,255,255,0.95)',
        padding: '12px 14px',
        borderRadius: '32px',
        boxShadow: '0 10px 35px rgba(0,0,0,0.12)',
        maxWidth: 'calc(100vw - 20px)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#f4f6f8', padding: '6px', borderRadius: '24px' }}>
          <button
            onClick={() => setTool('brush')}
            onMouseEnter={(e) => showTooltip('Pen', e)}
            onMouseLeave={hideTooltip}
            style={buttonStyle(tool === 'brush')}
            title="Pen"
          >
            ✏️
          </button>
          <button
            onClick={() => setTool('eraser')}
            onMouseEnter={(e) => showTooltip('Eraser', e)}
            onMouseLeave={hideTooltip}
            style={buttonStyle(tool === 'eraser')}
            title="Eraser"
          >
            🧽
          </button>
          <button
            onClick={() => setTool('text')}
            onMouseEnter={(e) => showTooltip('Text', e)}
            onMouseLeave={hideTooltip}
            style={buttonStyle(tool === 'text')}
            title="Text"
          >
            T
          </button>
          <button
            onClick={() => setTool('rectangle')}
            onMouseEnter={(e) => showTooltip('Rectangle', e)}
            onMouseLeave={hideTooltip}
            style={buttonStyle(tool === 'rectangle')}
            title="Rectangle"
          >
            ▭
          </button>
          <button
            onClick={() => setTool('square')}
            onMouseEnter={(e) => showTooltip('Square', e)}
            onMouseLeave={hideTooltip}
            style={buttonStyle(tool === 'square')}
            title="Square"
          >
            ■
          </button>
          <button
            onClick={() => setTool('select')}
            onMouseEnter={(e) => showTooltip('Select', e)}
            onMouseLeave={hideTooltip}
            style={buttonStyle(tool === 'select')}
            title="Select"
          >
            ⬤
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', borderRadius: '24px', backgroundColor: '#f4f6f8' }}>
          {colorOptions.map((color) => (
            <button
              key={color}
              onClick={() => {
                if (tool === 'text') setTextColor(color);
                else setBrushColor(color);
              }}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: color === (tool === 'text' ? textColor : brushColor) ? '2px solid #333' : '1px solid #ccc',
                backgroundColor: color,
                cursor: 'pointer'
              }}
              aria-label={`Select ${color} color`}
            />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '24px', backgroundColor: '#f4f6f8' }}>
          <span style={{ fontSize: '14px', color: '#333' }}>{tool === 'text' ? 'Text' : tool === 'eraser' ? 'Eraser' : 'Stroke'} size</span>
          <input
            type="range"
            min={tool === 'eraser' ? 5 : 1}
            max={tool === 'text' ? 60 : 60}
            value={tool === 'eraser' ? eraserSize : tool === 'text' ? textSize : brushSize}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (tool === 'eraser') setEraserSize(value);
              else if (tool === 'text') setTextSize(value);
              else setBrushSize(value);
            }}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px', color: '#333' }}>{tool === 'eraser' ? eraserSize : tool === 'text' ? textSize : brushSize}px</span>
        </div>

        {(tool === 'rectangle' || tool === 'square') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '24px', backgroundColor: '#f4f6f8' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={shapeFill}
                onChange={(e) => setShapeFill(e.target.checked)}
              />
              Fill
            </label>
            <input
              type="color"
              value={shapeFillColor}
              onChange={(e) => setShapeFillColor(e.target.value)}
              style={{ width: '32px', height: '32px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '24px', backgroundColor: '#f4f6f8' }}>
          <button onClick={() => goToPage(Math.max(0, currentPageIndex - 1))} style={navButtonStyle(currentPageIndex !== 0)} disabled={currentPageIndex === 0}>◀</button>
          <span style={{ minWidth: '100px', textAlign: 'center', fontSize: '14px', color: '#333' }}>Page {currentPageIndex + 1} / {pages.length}</span>
          <button onClick={() => goToPage(Math.min(pages.length - 1, currentPageIndex + 1))} style={navButtonStyle(currentPageIndex !== pages.length - 1)} disabled={currentPageIndex === pages.length - 1}>▶</button>
        </div>

        <button onClick={addPage} style={pillButtonStyle('#4caf50')}>+</button>
        <button onClick={deleteSelectedText} disabled={!selectedTextId} style={pillButtonStyle(selectedTextId ? '#f44336' : '#ddd', !selectedTextId)}>✕</button>
        <button onClick={clearCanvas} style={pillButtonStyle('#f44336')}>Clear</button>
      </div>
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            top: tooltip.y - 10,
            left: tooltip.x,
            transform: 'translate(-50%, -100%)',
            padding: '6px 10px',
            backgroundColor: 'rgba(33,33,33,0.9)',
            color: 'white',
            borderRadius: '999px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 30,
            whiteSpace: 'nowrap'
          }}
        >
          {tooltip.text}
        </div>
      )}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          overflow: 'hidden'
        }}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
      >
        <canvas
          onMouseDown={startDrawing}
          onMouseUp={stopDrawing}
          onMouseMove={draw}
          onMouseLeave={stopDrawing}
          onClick={handleCanvasClick}
          ref={canvasRef}
          style={{
            border: '1px solid #000',
            display: 'block',
            height: '100vh',
            width: '100%'
          }}
        />
        {textItems.map((item) => {
          const isSelected = item.id === selectedTextId;
          return (
            <div
              key={item.id}
              onMouseDown={(e) => handleTextMouseDown(e, item.id)}
              style={{
                position: 'absolute',
                left: item.x,
                top: item.y,
                color: item.color,
                fontSize: `${item.size}px`,
                userSelect: 'none',
                cursor: tool === 'select' ? 'move' : 'pointer',
                whiteSpace: 'pre-wrap',
                padding: isSelected ? '4px' : 0,
                border: isSelected ? '1px dashed #1976d2' : 'none',
                borderRadius: '4px',
                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
              }}
            >
              {item.text}
            </div>
          );
        })}
        {selectionBox && (
          <div
            style={{
              position: 'absolute',
              left: normalizeBox(selectionBox).x,
              top: normalizeBox(selectionBox).y,
              width: normalizeBox(selectionBox).width,
              height: normalizeBox(selectionBox).height,
              border: '1px dashed #1976d2',
              backgroundColor: 'rgba(25,118,210,0.1)',
              pointerEvents: 'none'
            }}
          />
        )}
        {(shapeBox && (tool === 'rectangle' || tool === 'square')) && (
          <div
            style={{
              position: 'absolute',
              left: normalizeBox(shapeBox).x,
              top: normalizeBox(shapeBox).y,
              width: normalizeBox(shapeBox).width,
              height: normalizeBox(shapeBox).height,
              border: '2px dashed #4caf50',
              backgroundColor: shapeFill ? `${shapeFillColor}33` : 'transparent',
              pointerEvents: 'none'
            }}
          />
        )}
        {activeTextInput && (
          <input
            ref={textInputRef}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitText();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelText();
              }
            }}
            placeholder="Type text and press Enter"
            style={{
              position: 'absolute',
              left: activeTextInput.x,
              top: activeTextInput.y,
              fontSize: `${textSize}px`,
              color: textColor,
              border: '1px solid #1976d2',
              borderRadius: '4px',
              padding: '4px 6px',
              outline: 'none',
              backgroundColor: 'rgba(255,255,255,0.9)'
            }}
          />
        )}
      </div>
    </div>
  );
}

export default App;
